import CompanyProfileService from './CompanyProfileService';
import JobSeekerProfileService from './JobSeekerProfileService';

// Global cache shared across all UserTypeService instances
const GLOBAL_USER_TYPE_CACHE = new Map();
const GLOBAL_IN_FLIGHT_REQUESTS = new Map();
const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

/**
 * Service for determining user types and managing user type-related operations
 */
class UserTypeService {
  constructor() {
    this.companyService = new CompanyProfileService();
    this.jobSeekerService = new JobSeekerProfileService();
  }

  /**
   * User type constants
   */
  static USER_TYPES = {
    COMPANY: 'company',
    JOB_SEEKER: 'job_seeker',
    UNKNOWN: 'unknown'
  };

  static PREFERRED_USER_TYPE_KEY = 'labourando_preferred_user_type';

  /**
   * Determines the user type based on their profiles
   * @param {string} userId - The user's ID
   * @returns {Promise<{userType: string, hasCompanyProfile: boolean, hasJobSeekerProfile: boolean}>}
   */
  async getUserType(userId) {
    // Check global cache first
    if (GLOBAL_USER_TYPE_CACHE.has(userId)) {
      const cached = GLOBAL_USER_TYPE_CACHE.get(userId);
      if (Date.now() - cached.timestamp < CACHE_TIMEOUT) {
        return cached.data;
      } else {
        // Cache expired, remove it
        GLOBAL_USER_TYPE_CACHE.delete(userId);
      }
    }

    // If request is already in flight, return that promise
    if (GLOBAL_IN_FLIGHT_REQUESTS.has(userId)) {
      return GLOBAL_IN_FLIGHT_REQUESTS.get(userId);
    }

    // Create the request promise
    const requestPromise = this._fetchUserTypeData(userId);
    
    // Store the in-flight request globally
    GLOBAL_IN_FLIGHT_REQUESTS.set(userId, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Remove from in-flight once complete
      GLOBAL_IN_FLIGHT_REQUESTS.delete(userId);
    }
  }

  /**
   * Internal method to fetch user type data from API
   * @private
   */
  async _fetchUserTypeData(userId) {
    try {
      const [companyProfile, jobSeekerProfile] = await Promise.all([
        this.companyService.getRecordsByUserId(userId),
        this.jobSeekerService.getRecordsByUserId(userId)
      ]);

      const hasCompanyProfile = !!companyProfile;
      const hasJobSeekerProfile = !!jobSeekerProfile;

      let userType = UserTypeService.USER_TYPES.UNKNOWN;

      if (hasCompanyProfile && !hasJobSeekerProfile) {
        userType = UserTypeService.USER_TYPES.COMPANY;
      } else if (hasJobSeekerProfile && !hasCompanyProfile) {
        userType = UserTypeService.USER_TYPES.JOB_SEEKER;
      } else if (hasJobSeekerProfile && hasCompanyProfile) {
        // Check for stored preference
        const storedPreference = localStorage.getItem(UserTypeService.PREFERRED_USER_TYPE_KEY);
        if (storedPreference && Object.values(UserTypeService.USER_TYPES).includes(storedPreference)) {
          userType = storedPreference;
        } else {
          // Default to JOB_SEEKER if no preference
          userType = UserTypeService.USER_TYPES.JOB_SEEKER;
        }
      }

      const result = {
        userType,
        hasCompanyProfile,
        hasJobSeekerProfile,
        profiles: {
          company: companyProfile,
          jobSeeker: jobSeekerProfile
        }
      };

      // Cache the result globally
      GLOBAL_USER_TYPE_CACHE.set(userId, {
        data: result,
        timestamp: Date.now()
      });

      return result;
    } catch (error) {
      console.error('Error determining user type:', error);
      return {
        userType: UserTypeService.USER_TYPES.UNKNOWN,
        hasCompanyProfile: false,
        hasJobSeekerProfile: false,
        profiles: {
          company: null,
          jobSeeker: null
        },
        error: error.message
      };
    }
  }

  /**
   * Clears the cached user type for a given user, forcing next getUserType() to fetch fresh data.
   * @param {string} userId - The user's ID
   */
  static clearCache(userId) {
    if (userId) {
      GLOBAL_USER_TYPE_CACHE.delete(userId);
      GLOBAL_IN_FLIGHT_REQUESTS.delete(userId);
    }
  }

  /**
   * Sets the preferred user type
   * @param {string} userType - The user type to set as preferred
   */
  setUserTypePreference(userType) {
    if (Object.values(UserTypeService.USER_TYPES).includes(userType)) {
      localStorage.setItem(UserTypeService.PREFERRED_USER_TYPE_KEY, userType);
    }
  }

  /**
   * Validates if user can perform company-specific actions
   * @param {string} userId - The user's ID
   * @returns {Promise<{canPerform: boolean, reason?: string}>}
   */
  async validateCompanyAccess(userId) {
    const result = await this.getUserType(userId);
    
    if (result.userType === UserTypeService.USER_TYPES.COMPANY) {
      return { canPerform: true };
    }
    
    return {
      canPerform: false,
      reason: result.userType === UserTypeService.USER_TYPES.JOB_SEEKER 
        ? 'Job seekers cannot perform company actions'
        : 'Please complete your company profile to access this feature'
    };
  }

  /**
   * Validates if user can perform job seeker-specific actions
   * @param {string} userId - The user's ID
   * @returns {Promise<{canPerform: boolean, reason?: string}>}
   */
  async validateJobSeekerAccess(userId) {
    const result = await this.getUserType(userId);
    
    if (result.userType === UserTypeService.USER_TYPES.JOB_SEEKER) {
      return { canPerform: true };
    }
    
    return {
      canPerform: false,
      reason: result.userType === UserTypeService.USER_TYPES.COMPANY 
        ? 'Companies cannot apply for jobs. Only job seekers can apply for positions.'
        : 'Please complete your job seeker profile to access this feature'
    };
  }

  /**
   * Gets appropriate dashboard route based on user type
   * @param {string} userId - The user's ID
   * @returns {Promise<string>}
   */
  async getDashboardRoute(userId) {
    const result = await this.getUserType(userId);
    
    switch (result.userType) {
      case UserTypeService.USER_TYPES.COMPANY:
        return '/dashboard';
      case UserTypeService.USER_TYPES.JOB_SEEKER:
        return '/dashboard';
      default:
        return '/onboarding';
    }
  }

}

export default UserTypeService;